import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import { Check, Shield, Lock, Eye, Link2, MessageSquare, Lightbulb, Settings, Rocket, HelpCircle, Mic, Briefcase, Clock, UserCheck, Zap, Play } from "lucide-react";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e0edc409-8280-4fb5-87c8-a9c376c387aa/artifacts/utrx7pg5_agents4you_light.svg";

// Employee Data with high-quality images
const TEAM_MEMBERS = [
  {
    name: "Sophie",
    role: "Assistentin der GF",
    image: "https://customer-assets.emergentagent.com/job_c3c18b75-6f1e-4f6d-8e0a-2c5d52a54b1d/artifacts/i1cpanvt_Sophie.png"
  },
  {
    name: "Erik",
    role: "Sales Manager",
    image: "https://customer-assets.emergentagent.com/job_c3c18b75-6f1e-4f6d-8e0a-2c5d52a54b1d/artifacts/k7vhwcx6_Erik.png"
  },
  {
    name: "Clara",
    role: "Bürokauffrau",
    image: "https://customer-assets.emergentagent.com/job_c3c18b75-6f1e-4f6d-8e0a-2c5d52a54b1d/artifacts/x9bonk0w_Clara.png"
  },
  {
    name: "Leonhard",
    role: "Controller",
    image: "https://customer-assets.emergentagent.com/job_e0edc409-8280-4fb5-87c8-a9c376c387aa/artifacts/7y5hi3re_Leonhard.jpeg"
  },
  {
    name: "Mia",
    role: "Marketing Managerin",
    image: "https://customer-assets.emergentagent.com/job_c3c18b75-6f1e-4f6d-8e0a-2c5d52a54b1d/artifacts/hapu5u2r_Mia.png"
  },
  {
    name: "Dr. Weber",
    role: "Unternehmensjurist",
    image: "https://customer-assets.emergentagent.com/job_c3c18b75-6f1e-4f6d-8e0a-2c5d52a54b1d/artifacts/2vcxsqa7_Dr.%20Weber.png"
  }
];

// CTA Component - Mobile optimized - Updated button text
const CTAButton = ({ className = "", showSubtext = true, subtextContent = "ca. 45–60 Min.", buttonText = null, mobileText = null }) => (
  <div className={`cta-box ${className}`}>
    <a
      href="mailto:phil@agents4you.ai?subject=Kostenloses%20Erstgespräch%20vereinbaren"
      className="gradient-btn"
      data-testid="cta-button"
    >
      <span className="cta-text-desktop">{buttonText || "Kostenloses Erstgespräch vereinbaren"}</span>
      <span className="cta-text-mobile">{mobileText || "Gratis Termin vereinbaren"}</span>
    </a>
    {showSubtext && <p className="cta-subtext">{subtextContent}</p>}
  </div>
);

// Auto-Sliding Employee Carousel Component
const EmployeeCarousel = ({ autoSlide = true, showTagline = false, darkMode = true }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    if (!autoSlide || isPaused || isDragging) return;
    
    const container = scrollRef.current;
    if (!container) return;

    const scrollSpeed = 0.5;
    let animationId;
    
    const animate = () => {
      if (container.scrollLeft >= container.scrollWidth - container.clientWidth - 10) {
        container.scrollLeft = 0;
      } else {
        container.scrollLeft += scrollSpeed;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [autoSlide, isPaused, isDragging]);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    setIsPaused(true);
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setTimeout(() => setIsPaused(false), 2000);
  };

  return (
    <div 
      className="carousel-wrapper"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => { setIsPaused(false); setIsDragging(false); }}
    >
      <div 
        ref={scrollRef}
        className={`carousel-track ${isDragging ? 'grabbing' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Duplicate items for infinite scroll effect */}
        {[...TEAM_MEMBERS, ...TEAM_MEMBERS].map((member, index) => (
          <div 
            key={index} 
            className={`carousel-card ${darkMode ? 'dark' : 'light'}`}
          >
            <div className="carousel-card-image">
              <img 
                src={member.image} 
                alt={member.name} 
                loading="lazy"
                draggable="false"
              />
            </div>
            <div className="carousel-card-name">{member.name}</div>
            <div className="carousel-card-role">{member.role}</div>
          </div>
        ))}
      </div>
      {showTagline && (
        <p className={`carousel-tagline ${darkMode ? 'dark' : 'light'}`}>
          Ihr digitales Team – sofort einsatzbereit
        </p>
      )}
    </div>
  );
};

// Mini Carousel for Mobile Hero - Auto-slide with manual touch/drag
const MiniEmployeeCarousel = ({ darkMode = true }) => {
  const scrollRef = useRef(null);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    let animationId;
    const scrollSpeed = 0.4;

    const animate = () => {
      const container = scrollRef.current;
      if (!container) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      container.scrollLeft += scrollSpeed;
      const loopPoint = container.scrollWidth / 2;
      if (container.scrollLeft >= loopPoint) {
        container.scrollLeft -= loopPoint;
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  const handleTouchStart = (e) => {
    setIsPaused(true);
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setTimeout(() => setIsPaused(false), 2000);
  };

  return (
    <div className="mini-carousel-wrapper">
      <div 
        ref={scrollRef}
        className="mini-carousel-track"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {[...TEAM_MEMBERS, ...TEAM_MEMBERS].map((member, index) => (
          <div key={`${member.name}-${index}`} className="mini-carousel-item">
            <div className="mini-avatar">
              <img src={member.image} alt={member.name} draggable="false" />
            </div>
            <span className={`mini-name ${darkMode ? '' : 'mini-name-light'}`}>{member.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Navbar Component - Mobile optimized
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`} data-testid="navbar">
      <div className="nav-container">
        <a href="#hero" data-testid="nav-logo">
          <img src={LOGO_URL} alt="agents2go" className="nav-logo" />
        </a>
        <div className={`nav-links ${menuOpen ? "open" : ""}`}>
          <a href="#problem" onClick={() => setMenuOpen(false)}>Problem</a>
          <a href="#solution" onClick={() => setMenuOpen(false)}>Lösung</a>
          <a href="#team" onClick={() => setMenuOpen(false)}>Team</a>
          <a href="#tasks" onClick={() => setMenuOpen(false)}>Aufgaben</a>
          <a href="#security" onClick={() => setMenuOpen(false)}>Sicherheit</a>
          <a
            href="mailto:phil@agents4you.ai?subject=Kostenloses%20Erstgespräch%20vereinbaren"
            className="nav-cta"
            data-testid="nav-cta"
          >
            Erstgespräch
          </a>
        </div>
        <button
          className={`mobile-menu-btn ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

// Hero Section - Mobile First with different layouts
const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <div className="hero-bg-gradient"></div>
      
      <div className="hero-content">
        <img src={LOGO_URL} alt="agents2go" className="hero-logo" data-testid="hero-logo" />
        
        <h1 className="hero-title">
          <span className="gradient-text">Ihr digitaler Mitarbeiter</span>
          <span className="hero-subtitle">individuell für Ihr Unternehmen</span>
        </h1>
        
        <p className="hero-text">
          {isMobile 
            ? "Übernimmt Aufgaben per Sprache oder Chat – ohne technisches Know-how."
            : "Ihr individueller digitaler Mitarbeiter übernimmt Aufgaben in Ihrem Unternehmen – einfach steuerbar per Sprache oder Chat, ganz ohne technisches Know-how."
          }
        </p>
        
        <div className="hero-cta">
          <a
            href="mailto:phil@agents4you.ai?subject=Kostenloses%20Erstgespräch%20vereinbaren"
            className="gradient-btn"
            data-testid="hero-cta"
          >
            <span className="btn-desktop">Kostenloses Erstgespräch</span>
            <span className="btn-mobile">Gratis Termin vereinbaren</span>
          </a>
          <a href="#video" className="hero-video-link" data-testid="hero-video-link">
            <Play size={14} /> In 90 Sekunden erklärt
          </a>
        </div>

        {/* Desktop: Large Carousel / Mobile: Mini Carousel */}
        <div className="hero-employees">
          {isMobile ? (
            <MiniEmployeeCarousel />
          ) : (
            <EmployeeCarousel autoSlide={true} showTagline={true} darkMode={true} />
          )}
        </div>
      </div>
    </section>
  );
};

// Animated Counter Component with staggered delay
const AnimatedCounter = ({ target, suffix = "%", delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          const startAnimation = () => {
            const duration = 1000;
            const startTime = performance.now();
            
            const animate = (currentTime) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const easeOut = 1 - Math.pow(1 - progress, 3);
              setCount(Math.floor(easeOut * target));
              if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          };

          if (delay > 0) {
            setTimeout(startAnimation, delay * 1000);
          } else {
            startAnimation();
          }
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, hasAnimated, delay]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Problem Section - Desktop: full text, Mobile: short text
const ProblemSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { 
      number: 82, 
      textDesktop: "der Unternehmen kämpfen mit Fachkräftemangel",
      textMobile: "Unternehmen kämpfen mit Fachkräftemangel",
      highlight: false,
      delay: 0
    },
    { 
      number: 45, 
      textDesktop: "der Aufgaben könnten automatisiert werden – werden aber noch manuell erledigt",
      textMobile: "der Aufgaben könnten automatisiert werden",
      highlight: true,
      delay: 0.15
    },
    { 
      number: 70, 
      textDesktop: "der Kosten vieler KMU sind Personalkosten",
      textMobile: "der Kosten vieler KMU sind Personalkosten",
      highlight: false,
      delay: 0.3
    }
  ];

  return (
    <section id="problem" ref={sectionRef} className="section section-light" data-testid="problem-section">
      <div className="container">
        <h2 className={`section-title ${isVisible ? 'visible' : ''}`}>
          {isMobile ? <>Warum viele Unternehmen <span className="gradient-text">nicht wachsen</span></> : "Warum Wachstum heute an operativen Aufgaben scheitert"}
        </h2>
        
        {isMobile && (
          <p className={`problem-subline ${isVisible ? 'visible' : ''}`}>
            Nicht Ideen fehlen – sondern Kapazität.
          </p>
        )}
        
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className={`stat-card ${stat.highlight ? 'highlight' : ''} ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: `${stat.delay}s` }}>
              <div className="stat-label">bis zu</div>
              <div className="stat-number"><AnimatedCounter target={stat.number} delay={stat.delay} /></div>
              <p className="stat-text">{isMobile ? stat.textMobile : stat.textDesktop}</p>
            </div>
          ))}
        </div>
        
        <div className={`problem-statement ${isVisible ? 'visible' : ''}`}>
          {!isMobile && (
            <p className="statement-main">
              Wachstum scheitert nicht an Ideen – sondern an fehlender Zeit und Ressourcen
            </p>
          )}
          <p className="statement-cta">
            {isMobile ? "Genau hier setzen digitale Mitarbeiter an" : "Genau hier setzt Ihr digitaler Mitarbeiter an."}
          </p>
          {isMobile && (
            <div className="problem-cta-mobile">
              <a
                href="mailto:phil@agents4you.ai?subject=Kostenloses%20Erstgespräch%20vereinbaren"
                className="gradient-btn"
                data-testid="problem-cta-mobile"
              >
                Gratis Termin vereinbaren
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Video Section - Between Problem and Solution
const VideoSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <section id="video" ref={sectionRef} className="section section-dark" data-testid="video-section">
      <div className="container">
        <h2 className={`section-title light ${isVisible ? 'visible' : ''}`}>
          Sehen Sie selbst, wie es funktioniert
        </h2>
        <p className={`section-subtitle light ${isVisible ? 'visible' : ''}`}>
          In 90 Sekunden erklärt
        </p>

        <div className={`video-wrapper ${isVisible ? 'visible' : ''}`}>
          <video
            ref={videoRef}
            className="video-player"
            src={isVisible ? `${process.env.PUBLIC_URL}/erklaervideo.mp4` : undefined}
            poster={`${process.env.PUBLIC_URL}/video-thumb.jpg`}
            controls={isPlaying}
            playsInline
            preload="none"
            data-testid="video-player"
          />
          {!isPlaying && (
            <button className="video-play-btn" onClick={handlePlay} aria-label="Video abspielen" data-testid="video-play-btn">
              <Play size={48} fill="white" />
            </button>
          )}
        </div>

        <CTAButton mobileText="Gratis Termin vereinbaren" subtextContent="Dauer ca. 45–60 Minuten." />
      </div>
    </section>
  );
};

// Solution Section - Desktop: full text, Mobile: short text
const SolutionSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { 
      icon: <Mic size={32} />, 
      title: "Einfach steuerbar", 
      textDesktop: "Per Sprache oder Chat – ganz ohne Prompting oder technisches Know-how"
    },
    { 
      icon: <Briefcase size={32} />, 
      title: "Übernimmt echte Aufgaben", 
      textDesktop: "Erledigt operative Tätigkeiten selbstständig und entlastet Ihr Team"
    },
    { 
      icon: <Clock size={32} />, 
      title: "24/7 verfügbar", 
      textDesktop: "Arbeitet rund um die Uhr – ohne Pause, ohne Ausfälle"
    },
    { 
      icon: <UserCheck size={32} />, 
      title: "Individuell für Ihr Unternehmen", 
      textDesktop: "Wird genau auf Ihre Prozesse und Anforderungen zugeschnitten"
    },
    { 
      icon: <Shield size={32} />, 
      title: "Sicher & kontrollierbar", 
      textDesktop: "Sie behalten jederzeit die Kontrolle – keine Aktion ohne Freigabe"
    },
    { 
      icon: <Zap size={32} />, 
      title: "Sofort einsatzbereit", 
      textDesktop: "Kein Setup von komplexen Tools – direkt nutzbar"
    }
  ];

  return (
    <section id="solution" ref={sectionRef} className="section section-dark" data-testid="solution-section">
      <div className="container">
        <h2 className={`section-title light ${isVisible ? 'visible' : ''}`}>
          So funktioniert Ihr <span className="gradient-text">digitaler Mitarbeiter</span>
        </h2>
        
        {!isMobile && (
          <p className={`section-subtitle light ${isVisible ? 'visible' : ''}`}>
            Sie steuern Ihren digitalen Mitarbeiter einfach per Sprache oder Chat – ohne Prompting, ohne technisches Wissen. Er übernimmt konkrete Aufgaben und entlastet Ihr Team sofort.
          </p>
        )}
        
        <div className={`features-grid ${isMobile ? 'features-grid-mobile' : ''}`}>
          {features.map((feature, index) => (
            <div key={index} className={`feature-card ${isMobile ? 'feature-card-mobile' : ''} ${isVisible ? 'visible' : ''}`}>
              <div className="feature-icon">{feature.icon}</div>
              <h4 className="feature-title">{feature.title}</h4>
              {!isMobile && <p className="feature-text">{feature.textDesktop}</p>}
            </div>
          ))}
        </div>

        <CTAButton subtextContent="Dauer ca. 60 Minuten." mobileText="Gratis Termin vereinbaren" />
      </div>
    </section>
  );
};

// Team Section - With Auto-Sliding Carousel
const TeamSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="section section-light" data-testid="team-section">
      <div className="container">
        <h2 className={`section-title ${isVisible ? 'visible' : ''}`}>
          Aus diesen Fähigkeiten entsteht Ihr <span className="gradient-text">digitaler Mitarbeiter</span>
        </h2>
        <p className={`section-subtitle ${isVisible ? 'visible' : ''}`}>
          {isMobile 
            ? "Kombinieren Sie die Fähigkeiten, die Ihr Unternehmen braucht."
            : "Sie kombinieren genau die Fähigkeiten, die Ihr Unternehmen braucht – wir konfigurieren daraus Ihren individuellen digitalen Mitarbeiter."
          }
        </p>
        
        {/* Auto-sliding Carousel with drag support */}
        <div className={`team-carousel-section ${isVisible ? 'visible' : ''}`}>
          <EmployeeCarousel autoSlide={true} showTagline={false} darkMode={false} />
        </div>

        {/* Highlight Card */}
        <div className={`highlight-card-wrapper ${isVisible ? 'visible' : ''}`}>
          <div className="highlight-card">
            <div className="highlight-icon-wrapper">
              <HelpCircle size={48} className="highlight-icon" />
            </div>
            <h3 className="highlight-title">Ihr Mitarbeiter</h3>
            <p className="highlight-subtitle">Individuell für Ihr Unternehmen</p>
            <p className="highlight-desc">
              {isMobile 
                ? "Exakt für Ihre Anforderungen zusammengestellt"
                : "Wird aus allen Fähigkeiten genau für Ihre Anforderungen zusammengestellt"
              }
            </p>
            <span className="highlight-badge">Ihr individueller digitaler Mitarbeiter</span>
          </div>
        </div>

        <CTAButton 
          buttonText="Ihren digitalen Mitarbeiter kennenlernen" 
          mobileText="Gratis Termin vereinbaren"
          showSubtext={false}
        />
      </div>
    </section>
  );
};

// Tasks Section - Desktop: full text, Mobile: short text
const TasksSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const tasks = [
    { 
      title: "Marketing", 
      itemsDesktop: ["Entwickelt Kampagnen & plant Content", "Steuert Performance Marketing & Social Media", "Analysiert KPIs & erstellt Reports"],
      itemsMobile: ["Kampagnen & Content", "Performance Marketing", "KPI-Analyse"]
    },
    { 
      title: "Vertrieb", 
      itemsDesktop: ["Qualifiziert Leads & bewertet Potenziale", "Erstellt Angebote & pflegt das CRM", "Steuert Follow-ups & die Pipeline"],
      itemsMobile: ["Lead-Qualifizierung", "Angebote & CRM", "Follow-ups"]
    },
    { 
      title: "Office", 
      itemsDesktop: ["Erstellt Dokumente & pflegt Daten", "Koordiniert Termine & Meetings", "Strukturiert interne Kommunikation & Protokolle"],
      itemsMobile: ["Dokumente & Daten", "Termine & Meetings", "Protokolle"]
    },
    { 
      title: "Controlling", 
      itemsDesktop: ["Erstellt Finanzreports & analysiert KPIs", "Überwacht Budgets & Kosten", "Erstellt Forecasts & bewertet Unternehmensperformance"],
      itemsMobile: ["Finanzreports", "Budgetüberwachung", "Forecasts"]
    },
    { 
      title: "Legal", 
      itemsDesktop: ["Prüft Verträge & identifiziert Risiken", "Verwaltet Dokumente & Verträge", "Überwacht Compliance & DSGVO"],
      itemsMobile: ["Vertragsprüfung", "Dokumentenverwaltung", "DSGVO-Compliance"]
    },
    { 
      title: "Assistenz der GF", 
      itemsDesktop: ["Koordiniert Termine & Prioritäten", "Bereitet Entscheidungsgrundlagen vor", "Analysiert Markt & Wettbewerb"],
      itemsMobile: ["Terminkoordination", "Entscheidungsvorlagen", "Marktanalyse"]
    }
  ];

  return (
    <section id="tasks" ref={sectionRef} className="section section-dark" data-testid="tasks-section">
      <div className="container">
        <h2 className={`section-title light ${isVisible ? 'visible' : ''}`}>
          Diese Aufgaben übernimmt Ihr <span className="gradient-text">digitaler Mitarbeiter</span>
        </h2>
        
        {!isMobile && (
          <p className={`section-subtitle light ${isVisible ? 'visible' : ''}`}>
            Ihr digitaler Mitarbeiter übernimmt genau die Aufgaben, die heute Zeit kosten – und schafft Raum für Wachstum.
          </p>
        )}
        
        <div className="tasks-grid">
          {tasks.map((task, index) => (
            <div key={index} className={`task-card ${isVisible ? 'visible' : ''}`} style={{ animationDelay: `${index * 0.05}s` }}>
              <h3 className="task-title">{task.title}</h3>
              <ul className="task-list">
                {(isMobile ? task.itemsMobile : task.itemsDesktop).map((item, i) => (
                  <li key={i}><Check size={14} /> {item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <CTAButton subtextContent="Dauer ca. 45–60 Minuten." mobileText="Gratis Termin vereinbaren" />
      </div>
    </section>
  );
};

// Security Section - Desktop: full text, Mobile: short text
const SecuritySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const securityFeatures = [
    { 
      icon: <Shield size={28} />, 
      title: "DSGVO & Infrastruktur", 
      textDesktop: "Ihre Daten werden DSGVO-konform verarbeitet und in einer sicheren Cloud-Infrastruktur gespeichert.",
      textMobile: "Sichere Cloud-Infrastruktur"
    },
    { 
      icon: <Lock size={28} />, 
      title: "Zugriffskontrolle", 
      textDesktop: "Ihr digitaler Mitarbeiter greift nur auf die Systeme und Daten zu, die für seine Aufgaben notwendig sind.",
      textMobile: "Nur bei Bedarf"
    },
    { 
      icon: <Eye size={28} />, 
      title: "Monitoring & Freigaben", 
      textDesktop: "Sie behalten die Kontrolle. Alle Aktivitäten werden dokumentiert. Kritische Aktionen benötigen immer Ihre Freigabe.",
      textMobile: "Freigabe für kritische Aktionen",
      highlight: true 
    },
    { 
      icon: <Link2 size={28} />, 
      title: "Integration", 
      textDesktop: "Ihr digitaler Mitarbeiter arbeitet direkt in Ihren bestehenden Tools und Prozessen.",
      textMobile: "In Ihren bestehenden Tools"
    }
  ];

  return (
    <section id="security" ref={sectionRef} className="section section-light" data-testid="security-section">
      <div className="container">
        <h2 className={`section-title security-title-gradient ${isVisible ? 'visible' : ''}`}>
          Volle Kontrolle. Maximale Sicherheit.
        </h2>
        
        {!isMobile && (
          <p className={`section-subtitle ${isVisible ? 'visible' : ''}`}>
            Ihr digitaler Mitarbeiter arbeitet ausschließlich in Ihren Systemen – und führt nur Aktionen aus, die Sie freigeben.
          </p>
        )}
        
        <div className="security-grid">
          {securityFeatures.map((feature, index) => (
            <div key={index} className={`security-card ${feature.highlight ? 'highlight' : ''} ${isVisible ? 'visible' : ''}`}>
              <div className="security-icon-centered">{feature.icon}</div>
              <h4 className="security-title">{feature.title}</h4>
              <p className="security-text">{isMobile ? feature.textMobile : feature.textDesktop}</p>
            </div>
          ))}
        </div>

        <p className={`security-statement ${isVisible ? 'visible' : ''}`}>
          Keine eigenständigen Entscheidungen. Keine unkontrollierten Aktionen.
        </p>

        <CTAButton subtextContent="Dauer ca. 45–60 Minuten." mobileText="Gratis Termin vereinbaren" />
      </div>
    </section>
  );
};

// Process Section - Desktop: full text, Mobile: short text
const ProcessSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    { 
      number: 1, 
      title: "Kostenloses Erstgespräch", 
      text: "Wir lernen Ihr Unternehmen kennen und identifizieren erste Potenziale."
    },
    { 
      number: 2, 
      title: "AI-Potenzial Analyse", 
      text: "Wir analysieren Ihre Prozesse und definieren den idealen digitalen Mitarbeiter."
    },
    { 
      number: 3, 
      title: "Individuelles Setup", 
      text: "Ihr digitaler Mitarbeiter wird konfiguriert und in Ihre Systeme eingebunden."
    },
    { 
      number: 4, 
      title: "Produktiver Einsatz", 
      text: "Ihr erster digitaler Mitarbeiter übernimmt Aufgaben und entlastet Ihr Team.",
      highlight: true 
    }
  ];

  return (
    <section id="process" ref={sectionRef} className="section section-dark" data-testid="process-section">
      <div className="container">
        <h2 className={`section-title light ${isVisible ? 'visible' : ''}`}>
          {isMobile ? "So starten Sie" : "So einfach starten Sie"}
        </h2>
        
        <p className={`section-subtitle light ${isVisible ? 'visible' : ''}`}>
          In wenigen Schritten zu Ihrem individuellen digitalen Mitarbeiter
        </p>
        
        {/* Desktop: horizontal with line / Mobile: vertical cards */}
        {isMobile ? (
          <div className="process-cards-mobile">
            {steps.map((step, index) => (
              <div key={index} className={`process-card-mobile ${step.highlight ? 'highlight' : ''} ${isVisible ? 'visible' : ''}`}>
                <div className="process-card-number">{step.number}</div>
                <h4 className="process-card-title">{step.title}</h4>
                <p className="process-card-text">{step.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className={`process-steps-desktop ${isVisible ? 'visible' : ''}`}>
            <div className="process-line"></div>
            {steps.map((step, index) => (
              <div key={index} className={`process-step-desktop ${step.highlight ? 'highlight' : ''}`}>
                <div className="step-number-desktop">{step.number}</div>
                <h4 className="step-title-desktop">{step.title}</h4>
                <p className="step-text-desktop">{step.text}</p>
              </div>
            ))}
          </div>
        )}

        <CTAButton 
          buttonText="Ihren digitalen Mitarbeiter kennenlernen" 
          mobileText="Gratis Termin vereinbaren"
          subtextContent="Dauer ca. 45–60 Minuten."
        />
      </div>
    </section>
  );
};

// Final CTA Section - Desktop: full text, Mobile: short text
const FinalCTASection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section section-light final-cta" data-testid="final-cta-section">
      <div className="container">
        <h2 className={`final-title ${isVisible ? 'visible' : ''}`}>
          Wo steckt bei Ihnen <span className="gradient-text nowrap">ungenutztes Potenzial?</span>
        </h2>
        
        <p className={`final-text ${isVisible ? 'visible' : ''}`}>
          Im kostenlosen Erstgespräch zeigen wir Ihnen, wo ein digitaler Mitarbeiter in Ihrem Unternehmen sofort Zeit spart und Wachstum ermöglicht.
        </p>

        <p className={`final-subtext ${isVisible ? 'visible' : ''}`}>
          Individuell für Ihr Unternehmen – ohne Vorbereitung für Sie.
        </p>
        
        <div className={`final-cta-btn ${isVisible ? 'visible' : ''}`}>
          <a
            href="mailto:phil@agents4you.ai?subject=Kostenloses%20Erstgespräch%20sichern"
            className="gradient-btn large"
            data-testid="final-cta-button"
          >
            <span className="btn-desktop">Kostenloses Erstgespräch sichern</span>
            <span className="btn-mobile">Gratis Termin vereinbaren</span>
          </a>
          <p className="cta-subtext">Dauer ca. 45–60 Minuten.</p>
        </div>
      </div>
    </section>
  );
};

// Footer - Clean layout
const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <footer className="footer" data-testid="footer">
      <div className={`footer-container ${isMobile ? 'footer-mobile' : 'footer-desktop'}`}>
        <img src={LOGO_URL} alt="Agents4You" className="footer-logo-large" />
        <p className="footer-copyright">
          © 2026 Agents4You
        </p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Navbar />
      <main>
        <HeroSection />
        <ProblemSection />
        <VideoSection />
        <SolutionSection />
        <TeamSection />
        <TasksSection />
        <SecuritySection />
        <ProcessSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
